import React, { useState, useCallback } from 'react';
import { DateRangePicker, CustomDatePicker } from '@jauntin/react-ui';
import PropTypes from 'prop-types';
import { Field, formValueSelector } from 'redux-form';
import { formName } from '../../Constants/constants';
import { useSelector } from 'react-redux';
import {
  requiredDates,
  atLeastOneDayMustBePicked,
} from '../../Helpers/validators';
import ButtonCollapse from '../ButtonCollapse';
import DaysOfWeekField from './DaysOfWeekField';
import EventFrequencyField from './EventFrequencyField';
import GuestsField from './GuestsField';
import HelpModal from './HelpModal';
import PerformersField from './PerformersField';
import GoodsField from './GoodsField';
import FoodField from './FoodField';
import ExhibitorsField from './ExhibitorsField';
import DateList from './DateList';
import { normalizeEventName } from '../../Helpers/normalizers';
import { TextInput, Button, useTimeTravel } from '@jauntin/react-ui';
import { validators } from '@jauntin/utilities';
import {
  disabledDatesForDateRangePicker,
  disabledDatesForCustomDatePicker,
} from '../../Helpers/DateHelpers';

const { notZero, minValue, required, arrayNotEmpty } = validators;

const minValue1 = minValue(1);

const maxAttendees = (value, allValues) => {
  return value * allValues.eventDates.length > 5000
    ? 'Total event attendance cannot exceed 5000'
    : undefined;
};

const Edit = ({
  enabled,
  confirm,
  deleteDate,
  show,
  valid,
  showDaysOfWeekField,
  showListOfDates,
  blockedDays,
  eventDates,
  eventTotalGuests,
  showDatePicker,
  showCustomDatePicker,
  mobileDisplay,
  additionalCoverageRef,
  scrollToPosition,
  venueUtcOffset,
  eventPerformersCount,
  eventGoods,
  eventFood,
  eventExhibition,
  performersFrequency,
  goodsVendorsFrequency,
  foodVendorsFrequency,
  exhibitorsFrequency,
  canManageSettings,
}) => {
  const hasDates = eventDates.length > 0;
  const [expandDateList, setExpandDateList] = useState(false);
  const [showModal, setShow] = useState(false);

  useTimeTravel(
    enabled && process.env.REACT_APP_ENV !== 'production' && canManageSettings,
  );

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const { startDate } = useSelector((state) =>
    formValueSelector(formName)(state, 'eventDateRange'),
  );

  const disabledDatesRange = useCallback(
    (day) => {
      return disabledDatesForDateRangePicker({
        blockedDays,
        startDate: new Date(startDate),
        venueUtcOffset,
      })(day);
    },
    [venueUtcOffset, startDate, blockedDays],
  );

  const disabledDatesCustom = useCallback(
    (day) =>
      disabledDatesForCustomDatePicker({
        venueUtcOffset,
        eventDates,
      })(day),
    [venueUtcOffset, eventDates],
  );

  if ((!hasDates || !show) && expandDateList)
    setExpandDateList(!expandDateList);
  return (
    show && (
      <>
        <Field
          component={TextInput}
          validate={[required]}
          normalize={normalizeEventName}
          disabled={!enabled}
          name="eventName"
          label="Please add a name for your event"
          ariaLabel="Event name"
          placeholder="Event name"
          maxLength={50}
          wrapperClassName="mb-1"
          inputClassName="form-control-lg"
          lengthClassName="eventName--charCount"
        />
        {(!expandDateList || !hasDates) && (
          <>
            <div className="form-group">
              <div className="row">
                <div className="col-12 col-xl-6">
                  <Field
                    component={EventFrequencyField}
                    validate={[notZero]}
                    name="eventFrequencyField"
                    type="select"
                    disabled={!enabled}
                  />
                </div>
                {showDaysOfWeekField && (
                  <div className="col-12 col-xl-6">
                    <DaysOfWeekField
                      name="daysOfWeekField"
                      validate={atLeastOneDayMustBePicked}
                    />
                  </div>
                )}
              </div>
              <div className="mb-2">
                In your selection, include the days required for setup and take
                down for the event.{' '}
                <span className="font-weight-bold">
                  If your event will extend past midnight, include the following
                  day.
                </span>
              </div>
              {showDatePicker && (
                <>
                  <Field
                    component={DateRangePicker}
                    validate={requiredDates}
                    disabledDates={disabledDatesRange}
                    name="eventDateRange"
                    numberOfMonths={mobileDisplay ? 1 : 2}
                    inputClassName="form-control-lg"
                  />
                </>
              )}
              {showCustomDatePicker && (
                <div className="d-inline-flex">
                  <Field
                    component={CustomDatePicker}
                    validate={arrayNotEmpty}
                    name="eventDates"
                    disabledDates={disabledDatesCustom}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {hasDates && showListOfDates && (
          <div className="mb-2">
            <ButtonCollapse
              clickFunction={() => setExpandDateList(!expandDateList)}
              showList={expandDateList}
              title={`${eventDates.length} date(s) selected`}
            />

            {expandDateList && (
              <DateList dates={eventDates} deleteDate={deleteDate} />
            )}
          </div>
        )}

        <Field
          component={GuestsField}
          validate={[required, minValue1, maxAttendees]}
          name="eventDailyGuests"
          disabled={!enabled}
          eventTotalGuests={eventTotalGuests}
          eventDates={eventDates}
          mobileDisplay={mobileDisplay}
        />

        <div className="d-flex align-items-center mb-2">
          <h4 className="m-0">Performers, Vendors and Exhibitors</h4>
          <span
            tabIndex={!enabled ? '-1' : '0'}
            className="span-link ml-2 small"
            role="button"
            onClick={() => handleShow()}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')
                handleShow();
            }}
          >
            Help with this section.
          </span>
        </div>
        <div className="mb-4">
          Leave at zero if there will be none, or, if they will provide their
          own insurance. This coverage offers protection for{' '}
          <span className="font-weight-bold">you</span>, should a claim arise as
          a result of uninsured performer, vendor, or exhibitor negligence.
          Depending on your event, only some or none of these categories will
          apply.
        </div>

        <PerformersField
          eventPerformersCount={eventPerformersCount}
          eventDates={eventDates}
          performersFrequency={performersFrequency}
        />

        <GoodsField
          eventGoods={eventGoods}
          eventDates={eventDates}
          goodsVendorsFrequency={goodsVendorsFrequency}
        />

        <FoodField
          eventFood={eventFood}
          eventDates={eventDates}
          foodVendorsFrequency={foodVendorsFrequency}
        />

        <ExhibitorsField
          eventExhibition={eventExhibition}
          eventDates={eventDates}
          exhibitorsFrequency={exhibitorsFrequency}
        />
        <hr />
        <div className="float-right">
          <Button
            disabled={!valid}
            onClick={() => {
              confirm();
              scrollToPosition(additionalCoverageRef);
            }}
            text="Next"
            className="btn-primary"
          />
        </div>
        <HelpModal showModal={showModal} handleClose={handleClose} />
      </>
    )
  );
};
Edit.propTypes = {
  enabled: PropTypes.bool.isRequired,
  confirm: PropTypes.func.isRequired,
  deleteDate: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  showDaysOfWeekField: PropTypes.bool.isRequired,
  showListOfDates: PropTypes.bool.isRequired,
  blockedDays: PropTypes.arrayOf(PropTypes.number).isRequired,
  eventDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  eventTotalGuests: PropTypes.string.isRequired,
  showDatePicker: PropTypes.bool.isRequired,
  showCustomDatePicker: PropTypes.bool.isRequired,
  mobileDisplay: PropTypes.bool.isRequired,
  scrollToPosition: PropTypes.func.isRequired,
  additionalCoverageRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  venueUtcOffset: PropTypes.number.isRequired,
  eventPerformersCount: PropTypes.string.isRequired,
  eventGoods: PropTypes.string.isRequired,
  eventFood: PropTypes.string.isRequired,
  eventExhibition: PropTypes.string.isRequired,
  performersFrequency: PropTypes.string.isRequired,
  goodsVendorsFrequency: PropTypes.string.isRequired,
  foodVendorsFrequency: PropTypes.string.isRequired,
  exhibitorsFrequency: PropTypes.string.isRequired,
  canManageSettings: PropTypes.bool.isRequired,
};

export default Edit;
